import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button, Icon } from '@advanza/ui'
import Card from 'components/Card'
import Page from 'components/ui/Page'
import { useCurrentCountry } from 'hooks/miscHooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    buildQueryString,
    defaultEndDate,
    defaultStartDate,
    ENDPOINT,
    EXPORT_ENDPOINT,
    formatMinutes,
    formatPercent,
} from './aiAutoReplyAnalyticsReportUtils'

interface CountryAdoption {
    country: string
    active_ai: number
    active_standard: number
}

interface AdoptionBucket {
    active_total: number
    active_ai: number
    active_standard: number
    ever_enabled_ai: number
    ai_share_pct: number
    by_country: CountryAdoption[]
}

interface TrendBucket {
    period: string
    ai: number
    standard: number
}

interface ResponseRateBucket {
    sent: number
    demand_replied: number
    response_rate_pct: number
    avg_minutes_to_reply: number | null
    median_minutes_to_reply: number | null
    takeover_rate_pct: number
    avg_body_length: number
}

interface BackstopBucket {
    reply_type: string
    backstop_delay: number
    sent: number
    demand_replied: number
    response_rate_pct: number
}

interface FiltersApplied {
    start_date: string
    end_date: string
    country: string | null
    provider_id: number | null
}

interface AiAutoReplyAnalyticsReport {
    filters_applied: FiltersApplied
    adoption: AdoptionBucket
    adoption_trend: TrendBucket[]
    response_rates: Record<string, ResponseRateBucket>
    backstop: BackstopBucket[]
}

const AiAutoReplyAnalyticsReportPage = () => {
    const country = useCurrentCountry()
    const [startDate, setStartDate] = useState<string>(defaultStartDate())
    const [endDate, setEndDate] = useState<string>(defaultEndDate())
    const [providerId, setProviderId] = useState<string>('')
    const [scopeCountry, setScopeCountry] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>('')
    const [report, setReport] = useState<AiAutoReplyAnalyticsReport | null>(null)
    const [isExporting, setIsExporting] = useState<boolean>(false)

    const effectiveCountry = useMemo<string | null>(() => {
        if (!scopeCountry) {
            return null
        }
        return country ? country.toString().toUpperCase() : null
    }, [country, scopeCountry])

    const loadReport = useCallback(async () => {
        setLoading(true)
        setError('')
        setReport(null)
        try {
            const queryString = buildQueryString(startDate, endDate, effectiveCountry, providerId)
            const url = queryString === '' ? ENDPOINT : `${ENDPOINT}?${queryString}`
            const response = await call(url)
            if (response?.status === 'success' && response?.adoption) {
                setReport(response as unknown as AiAutoReplyAnalyticsReport)
            } else {
                setError(response?.error || 'Failed to load report')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }, [startDate, endDate, effectiveCountry, providerId])

    const handleExport = useCallback(() => {
        if (isExporting) {
            return
        }
        setIsExporting(true)
        const queryString = buildQueryString(startDate, endDate, effectiveCountry, providerId)
        const base = `${window.location.origin}/api/${EXPORT_ENDPOINT}`
        const url = queryString === '' ? base : `${base}?${queryString}`
        window.open(url, '_blank', 'noopener,noreferrer')
        window.setTimeout(() => setIsExporting(false), 1500)
    }, [startDate, endDate, effectiveCountry, providerId, isExporting])

    useEffect(() => {
        loadReport()
    }, [])

    const standard = report?.response_rates.standard
    const ai = report?.response_rates.ai

    return (
        <Page title="AI Auto Reply Analytics">
            <div className="space-y-5">
                <div>
                    <h1 className="text-2xl font-semibold">AI Auto Reply Analytics</h1>
                    <p className="text-grey-700 mt-1">
                        Adoption of the auto reply feature and a comparison of standard vs AI auto
                        replies on demand response rate and time-to-reply.
                    </p>
                </div>

                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-grey-200 bg-white p-4">
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Start date
                        </label>
                        <InputField
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            End date
                        </label>
                        <InputField
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Provider ID
                        </label>
                        <InputField
                            type="number"
                            placeholder="All providers"
                            value={providerId}
                            onChange={(e) => setProviderId(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Country scope
                        </label>
                        <label className="flex items-center gap-2 text-md text-grey-700">
                            <input
                                type="checkbox"
                                checked={scopeCountry}
                                onChange={(e) => setScopeCountry(e.target.checked)}
                            />
                            <span>
                                Limit to <strong>{country?.toString().toUpperCase() || '?'}</strong>
                            </span>
                        </label>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={loadReport}
                            disabled={loading}
                            before={<Icon name="refresh" />}>
                            {loading ? 'Loading…' : 'Apply'}
                        </Button>
                        <Button
                            onClick={handleExport}
                            disabled={isExporting || loading || !report}
                            before={<Icon name="file_download" />}>
                            Export CSV
                        </Button>
                    </div>
                </div>

                {error !== '' && (
                    <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                        {error}
                    </div>
                )}

                {loading && !report && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        <p className="mt-3 text-md text-grey-500">Loading auto reply analytics…</p>
                    </div>
                )}

                {report && (
                    <div className="space-y-5">
                        <p className="text-md text-grey-500">
                            Date range:{' '}
                            <span className="font-medium text-grey-700">
                                {report.filters_applied.start_date}
                            </span>
                            {' → '}
                            <span className="font-medium text-grey-700">
                                {report.filters_applied.end_date}
                            </span>
                            {report.filters_applied.country !== null && (
                                <>
                                    {' · '}Country:{' '}
                                    <span className="font-medium text-grey-700">
                                        {report.filters_applied.country}
                                    </span>
                                </>
                            )}
                            {report.filters_applied.provider_id !== null && (
                                <>
                                    {' · '}Provider:{' '}
                                    <span className="font-medium text-grey-700">
                                        {report.filters_applied.provider_id}
                                    </span>
                                </>
                            )}
                        </p>

                        <Card header="Adoption (current snapshot)">
                            <p className="text-sm text-grey-700 mb-3">
                                Counts reflect the current state of auto reply settings and are not
                                affected by the selected date range.
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                <KpiTile
                                    label="Active auto reply pros"
                                    value={report.adoption.active_total}
                                    color="#2563eb"
                                />
                                <KpiTile
                                    label="Active AI pros"
                                    value={report.adoption.active_ai}
                                    color="#7c3aed"
                                />
                                <KpiTile
                                    label="Active standard pros"
                                    value={report.adoption.active_standard}
                                    color="#0891b2"
                                />
                                <KpiTile
                                    label="AI share"
                                    value={formatPercent(report.adoption.ai_share_pct)}
                                    color="#16a34a"
                                    sub="of active auto reply"
                                />
                                <KpiTile
                                    label="Ever enabled AI"
                                    value={report.adoption.ever_enabled_ai}
                                    color="#0d9488"
                                />
                            </div>
                        </Card>

                        {report.adoption.by_country.length > 0 && (
                            <Card header="Adoption by country (active pros)">
                                <table className="w-full text-md">
                                    <thead>
                                        <tr className="text-left text-grey-500">
                                            <th className="py-1.5 font-medium">Country</th>
                                            <th className="py-1.5 font-medium text-right">AI</th>
                                            <th className="py-1.5 font-medium text-right">
                                                Standard
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.adoption.by_country.map((row) => (
                                            <tr
                                                key={row.country}
                                                className="border-t border-grey-100">
                                                <td className="py-1.5 text-grey-700">
                                                    {row.country}
                                                </td>
                                                <td className="py-1.5 text-right font-bold tabular-nums text-grey-900">
                                                    {row.active_ai}
                                                </td>
                                                <td className="py-1.5 text-right font-bold tabular-nums text-grey-900">
                                                    {row.active_standard}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Card>
                        )}

                        <Card header="Standard vs AI — demand engagement">
                            <p className="text-sm text-grey-700 mb-3">
                                Based on auto replies actually delivered in the selected date range.
                                Response rate = share of demands that sent a human reply afterwards.
                                Time-to-reply is measured only over demands that replied. Pro
                                takeover = a manual pro message after the auto reply.
                            </p>
                            <table className="w-full text-md">
                                <thead>
                                    <tr className="text-left text-grey-500">
                                        <th className="py-1.5 font-medium">Metric</th>
                                        <th className="py-1.5 font-medium text-right">Standard</th>
                                        <th className="py-1.5 font-medium text-right">AI</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <ComparisonRow
                                        label="Auto replies sent"
                                        standard={standard?.sent ?? 0}
                                        ai={ai?.sent ?? 0}
                                    />
                                    <ComparisonRow
                                        label="Demand replied"
                                        standard={standard?.demand_replied ?? 0}
                                        ai={ai?.demand_replied ?? 0}
                                    />
                                    <ComparisonRow
                                        label="Response rate"
                                        standard={formatPercent(standard?.response_rate_pct ?? 0)}
                                        ai={formatPercent(ai?.response_rate_pct ?? 0)}
                                    />
                                    <ComparisonRow
                                        label="Avg time to reply"
                                        standard={formatMinutes(
                                            standard?.avg_minutes_to_reply ?? null,
                                        )}
                                        ai={formatMinutes(ai?.avg_minutes_to_reply ?? null)}
                                    />
                                    <ComparisonRow
                                        label="Median time to reply"
                                        standard={formatMinutes(
                                            standard?.median_minutes_to_reply ?? null,
                                        )}
                                        ai={formatMinutes(ai?.median_minutes_to_reply ?? null)}
                                    />
                                    <ComparisonRow
                                        label="Pro takeover rate"
                                        standard={formatPercent(standard?.takeover_rate_pct ?? 0)}
                                        ai={formatPercent(ai?.takeover_rate_pct ?? 0)}
                                    />
                                    <ComparisonRow
                                        label="Avg message length"
                                        standard={Math.round(standard?.avg_body_length ?? 0)}
                                        ai={Math.round(ai?.avg_body_length ?? 0)}
                                    />
                                </tbody>
                            </table>
                        </Card>

                        {report.backstop.length > 0 && (
                            <Card header="Backstop delay breakdown">
                                <p className="text-sm text-grey-700 mb-3">
                                    Auto replies delivered in range grouped by configured backstop
                                    delay (hours) and reply type.
                                </p>
                                <table className="w-full text-md">
                                    <thead>
                                        <tr className="text-left text-grey-500">
                                            <th className="py-1.5 font-medium">Type</th>
                                            <th className="py-1.5 font-medium text-right">
                                                Delay (h)
                                            </th>
                                            <th className="py-1.5 font-medium text-right">Sent</th>
                                            <th className="py-1.5 font-medium text-right">
                                                Replied
                                            </th>
                                            <th className="py-1.5 font-medium text-right">
                                                Response rate
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.backstop.map((row) => (
                                            <tr
                                                key={`${row.reply_type}-${row.backstop_delay}`}
                                                className="border-t border-grey-100">
                                                <td className="py-1.5 text-grey-700">
                                                    {row.reply_type}
                                                </td>
                                                <td className="py-1.5 text-right tabular-nums text-grey-700">
                                                    {row.backstop_delay}
                                                </td>
                                                <td className="py-1.5 text-right tabular-nums text-grey-900">
                                                    {row.sent}
                                                </td>
                                                <td className="py-1.5 text-right tabular-nums text-grey-900">
                                                    {row.demand_replied}
                                                </td>
                                                <td className="py-1.5 text-right font-bold tabular-nums text-grey-900">
                                                    {formatPercent(row.response_rate_pct)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Card>
                        )}

                        {report.adoption_trend.length > 0 && (
                            <Card header="Adoption trend (auto replies created)">
                                <p className="text-sm text-grey-700 mb-3">
                                    Approximate: based on when the auto reply row was created, not
                                    on later changes to the reply type.
                                </p>
                                <table className="w-full text-md">
                                    <thead>
                                        <tr className="text-left text-grey-500">
                                            <th className="py-1.5 font-medium">Period</th>
                                            <th className="py-1.5 font-medium text-right">AI</th>
                                            <th className="py-1.5 font-medium text-right">
                                                Standard
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.adoption_trend.map((row) => (
                                            <tr
                                                key={row.period}
                                                className="border-t border-grey-100">
                                                <td className="py-1.5 text-grey-700">
                                                    {row.period}
                                                </td>
                                                <td className="py-1.5 text-right font-bold tabular-nums text-grey-900">
                                                    {row.ai}
                                                </td>
                                                <td className="py-1.5 text-right tabular-nums text-grey-700">
                                                    {row.standard}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Card>
                        )}
                    </div>
                )}
            </div>
        </Page>
    )
}

interface KpiTileProps {
    label: string
    value: number | string
    color: string
    sub?: string
}

const KpiTile = ({ label, value, color, sub }: KpiTileProps) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center flex flex-col justify-center">
        <div className="text-2xl font-bold tabular-nums" style={{ color }}>
            {value}
        </div>
        <div className="text-sm font-medium text-slate-500 mt-0.5">{label}</div>
        {sub && <div className="text-sm text-slate-400 mt-0.5">{sub}</div>}
    </div>
)

interface ComparisonRowProps {
    label: string
    standard: number | string
    ai: number | string
}

const ComparisonRow = ({ label, standard, ai }: ComparisonRowProps) => (
    <tr className="border-t border-grey-100 first:border-t-0">
        <td className="py-1.5 text-grey-700">{label}</td>
        <td className="py-1.5 pl-4 text-right font-bold tabular-nums text-grey-900 whitespace-nowrap">
            {standard}
        </td>
        <td className="py-1.5 pl-4 text-right font-bold tabular-nums text-grey-900 whitespace-nowrap">
            {ai}
        </td>
    </tr>
)

export default AiAutoReplyAnalyticsReportPage
